import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Mail, Trash2, Copy, Download, Pencil, Files, RefreshCw, History, Loader2, Undo2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function DocumentsListPage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(null); // "save" | "duplicate" | "regenerate"
  const [versions, setVersions] = useState(null); // null = hidden, [] = shown

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
    } catch (err) {
      console.error("Failed to load documents", err);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDoc = (d) => { setOpen(d); setEditing(false); setVersions(null); };
  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    toast.success("Deleted");
    load();
  };
  const copy = async (txt) => {
    await navigator.clipboard.writeText(txt);
    toast.success("Copied");
  };
  const download = (doc) => {
    window.open(`${API}/documents/${doc.doc_id}/pdf`, "_blank");
  };

  const saveEdit = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/documents/${open.doc_id}`, { content: draft });
      setOpen(data);
      setEditing(false);
      toast.success("Saved — previous version kept in history");
      load();
    } catch { toast.error("Could not save"); } finally { setBusy(null); }
  };

  const duplicate = async () => {
    setBusy("duplicate");
    try {
      await api.post(`/documents/${open.doc_id}/duplicate`);
      toast.success("Duplicated");
      setOpen(null);
      load();
    } catch { toast.error("Could not duplicate"); } finally { setBusy(null); }
  };

  const regenerate = async () => {
    setBusy("regenerate");
    try {
      const { data } = await api.post(`/documents/${open.doc_id}/regenerate`, {});
      setOpen(data);
      toast.success("AI rewrote your document — old version kept in history");
      load();
    } catch { toast.error("Could not regenerate"); } finally { setBusy(null); }
  };

  const showHistory = async () => {
    try {
      const { data } = await api.get(`/documents/${open.doc_id}/versions`);
      setVersions(data);
      if (data.length === 0) toast.info("No previous versions yet");
    } catch { toast.error("Could not load history"); }
  };

  const restore = async (index) => {
    try {
      const { data } = await api.post(`/documents/${open.doc_id}/versions/${index}/restore`);
      setOpen(data);
      setVersions(null);
      toast.success("Version restored");
      load();
    } catch { toast.error("Could not restore"); }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Documents</p>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Your saved documents</h1>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="docs-new-cv"><Link to="/builder/cv"><FileText className="mr-1 h-4 w-4" /> New CV</Link></Button>
          <Button asChild data-testid="docs-new-letter"><Link to="/builder/letter"><Mail className="mr-1 h-4 w-4" /> New letter</Link></Button>
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-card">
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No documents yet. Build your first CV or letter above.</div>
        ) : (
          <div className="divide-y divide-border">
            {docs.map((d) => (
              <div key={d.doc_id} className="flex items-center justify-between gap-3 p-4">
                <button onClick={() => openDoc(d)} className="min-w-0 flex-1 text-left" data-testid={`doc-${d.doc_id}`}>
                  <div className="flex items-center gap-2">
                    {d.kind === "cv" ? <FileText className="h-4 w-4 text-primary" /> : <Mail className="h-4 w-4 text-primary" />}
                    <div className="truncate text-sm font-medium">{d.title}</div>
                    {(d.versions?.length || 0) > 0 && (
                      <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">v{d.versions.length + 1}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground capitalize">{d.kind.replace("_", " ")} · {new Date(d.created_at).toLocaleString()}</div>
                </button>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => copy(d.content)} data-testid={`copy-${d.doc_id}`}><Copy className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => download(d)} data-testid={`download-${d.doc_id}`}><Download className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(d.doc_id)} data-testid={`delete-${d.doc_id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{open?.title}</DialogTitle>
            <DialogDescription>
              {open ? `Saved ${new Date(open.created_at).toLocaleString()}` : ""} · Living document — edit, regenerate, or restore any version.
            </DialogDescription>
          </DialogHeader>
          {open && !versions && (
            <>
              {editing ? (
                <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={18}
                  className="max-h-[55vh] font-mono text-xs leading-relaxed" data-testid="doc-edit-textarea" />
              ) : (
                <pre className="max-h-[55vh] overflow-auto whitespace-pre-wrap rounded-md border border-border bg-background p-4 font-mono text-xs leading-relaxed" data-testid="doc-content">{open.content}</pre>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                {editing ? (
                  <>
                    <Button size="sm" onClick={saveEdit} disabled={busy === "save"} data-testid="doc-save-btn">
                      {busy === "save" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null} Save changes
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => { setDraft(open.content); setEditing(true); }} data-testid="doc-edit-btn">
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={regenerate} disabled={busy === "regenerate"} data-testid="doc-regenerate-btn">
                      {busy === "regenerate" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />} AI improve
                    </Button>
                    <Button size="sm" variant="outline" onClick={duplicate} disabled={busy === "duplicate"} data-testid="doc-duplicate-btn">
                      <Files className="mr-1 h-3.5 w-3.5" /> Duplicate
                    </Button>
                    <Button size="sm" variant="outline" onClick={showHistory} data-testid="doc-history-btn">
                      <History className="mr-1 h-3.5 w-3.5" /> History
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copy(open.content)}><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
                    <Button size="sm" onClick={() => download(open)}><Download className="mr-1 h-3.5 w-3.5" /> PDF</Button>
                  </>
                )}
              </div>
            </>
          )}
          {open && versions && (
            <div data-testid="doc-versions">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Version history ({versions.length})</p>
                <Button size="sm" variant="ghost" onClick={() => setVersions(null)}>← Back to document</Button>
              </div>
              {versions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No previous versions yet — edit or AI-improve this document to create one.</p>
              ) : (
                <div className="max-h-[55vh] space-y-2 overflow-auto">
                  {versions.map((v) => (
                    <div key={v.index} className="rounded-xl border border-border bg-background p-3" data-testid={`version-${v.index}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground">{new Date(v.saved_at).toLocaleString()}</span>
                        <Button size="sm" variant="outline" onClick={() => restore(v.index)} data-testid={`restore-${v.index}`}>
                          <Undo2 className="mr-1 h-3.5 w-3.5" /> Restore
                        </Button>
                      </div>
                      <p className="mt-2 line-clamp-3 whitespace-pre-wrap font-mono text-[11px] text-muted-foreground">{v.preview}…</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
